import { type FormEvent, useEffect, useState } from 'react'
import { Camera, Play, Trash2 } from 'lucide-react'
import type { MediaItem } from '@cht/shared'
import type { FieldClient } from '../core/api/field-client.js'
import type { MediaStore } from '../core/capture/media-store.js'
import { Button, ConfirmSheet, InlineNotice } from './components.js'

export function MediaLibrary({ client, localStore, userId, admin, onSelectLocation, onChanged }: { client: FieldClient; localStore?: MediaStore; userId: string; admin: boolean; onSelectLocation?: (coordinates: { latitude: number; longitude: number } | undefined) => void; onChanged?: () => Promise<void> }) {
  const [items, setItems] = useState<MediaItem[]>([])
  const [filter, setFilter] = useState<'all' | 'photo' | 'video'>('all')
  const [selected, setSelected] = useState<MediaItem>()
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(true)
  const [videoFailed, setVideoFailed] = useState(false)
  const [error, setError] = useState<string>()

  async function load() {
    setLoading(true); setError(undefined)
    try {
      const loaded = await client.media(filter === 'all' ? undefined : filter)
      setItems(loaded); setSelected((current) => current ? loaded.find((item) => item.id === current.id) : undefined)
    } catch { setError('Property media could not be loaded. Check the connection and try again.') }
    finally { setLoading(false) }
  }
  useEffect(() => { void load() }, [client, filter])

  function select(item: MediaItem) {
    setSelected(item); setTitle(item.title); setDescription(item.description); setEditing(false); setConfirmDelete(false); setVideoFailed(false)
    onSelectLocation?.(item.coordinates)
  }

  async function save(event: FormEvent) {
    event.preventDefault(); if (!selected) return
    setError(undefined)
    try {
      const updated = await client.updateMedia(selected.id, { title: title.trim(), description: description.trim() })
      await localStore?.updateLocalDetails(updated.id, updated.title, updated.description)
      setSelected(updated); setItems((current) => current.map((item) => item.id === updated.id ? updated : item)); setEditing(false)
      await onChanged?.()
    } catch { setError('Media details could not be saved.') }
  }

  async function remove() {
    if (!selected) return
    setError(undefined)
    try {
      await client.deleteMedia(selected.id); await localStore?.removeLocal(selected.id); setItems((current) => current.filter((item) => item.id !== selected.id)); setSelected(undefined); setConfirmDelete(false); onSelectLocation?.(undefined); await onChanged?.()
    } catch { setError('The media item could not be deleted. It is still stored.') }
  }

  const canManage = selected && (admin || selected.authorId === userId)
  if (selected) return <div className="media-library media-detail">
    <Button type="button" variant="ghost" onClick={() => setSelected(undefined)}>Back to media</Button>
    {selected.kind === 'photo'
      ? <img src={selected.viewUrl ?? selected.originalUrl} alt={selected.title || 'Property photo'} />
      : videoFailed
        ? <InlineNotice tone="fail" title="This video cannot play here" action={<a className="button secondary" href={selected.originalUrl} download>Download original</a>}>The original is still stored. Download it to open it in another player.</InlineNotice>
        : <video src={selected.originalUrl} controls playsInline aria-label={selected.title || 'Property video'} onError={() => setVideoFailed(true)} />}
    {editing ? <form className="media-edit" onSubmit={(event) => void save(event)}>
      <InlineNotice tone="info" title="Details only">The original {selected.kind} and capture time cannot be changed.</InlineNotice>
      <label htmlFor="library-media-title">Title</label><input id="library-media-title" maxLength={120} value={title} onChange={(event) => setTitle(event.target.value)} />
      <label htmlFor="library-media-notes">Notes</label><textarea id="library-media-notes" maxLength={2000} value={description} onChange={(event) => setDescription(event.target.value)} />
      <div className="button-row"><Button type="button" variant="secondary" onClick={() => setEditing(false)}>Cancel</Button><Button type="submit">Save details</Button></div>
    </form> : <>
      <p className="data">{selected.kind.toUpperCase()} · {formatBytes(selected.sizeBytes)} · {new Date(selected.capturedAt).toLocaleString()}</p>
      <h2>{selected.title || `${selected.kind === 'photo' ? 'Photo' : 'Video'} capture`}</h2>
      {selected.description && <p>{selected.description}</p>}
      <p className="data">{selected.coordinates ? `${selected.coordinates.latitude.toFixed(5)}, ${selected.coordinates.longitude.toFixed(5)}` : 'NO LOCATION ATTACHED'}</p>
      {canManage && <div className="button-row"><Button type="button" variant="secondary" onClick={() => setEditing(true)}>Edit details</Button><Button type="button" variant="ghost" icon={Trash2} onClick={() => setConfirmDelete(true)}>Delete</Button></div>}
      {confirmDelete && <ConfirmSheet title={`Delete ${selected.title || `this ${selected.kind}`}?`} onCancel={() => setConfirmDelete(false)} actions={<><Button type="button" variant="secondary" onClick={() => setConfirmDelete(false)}>Keep it</Button><Button type="button" variant="danger" onClick={() => void remove()}>Delete</Button></>}>This permanently deletes the original and frees {formatBytes(selected.sizeBytes)}. It cannot be undone.</ConfirmSheet>}
    </>}
    {error && <InlineNotice tone="fail">{error}</InlineNotice>}
  </div>

  return <div className="media-library">
    <div className="media-filter" aria-label="Media type">
      {(['all', 'photo', 'video'] as const).map((kind) => <Button type="button" size="sm" key={kind} variant={filter === kind ? 'primary' : 'secondary'} onClick={() => setFilter(kind)}>{kind === 'all' ? 'All' : `${kind}s`}</Button>)}
    </div>
    {error && <InlineNotice tone="fail" action={<Button type="button" variant="secondary" onClick={() => void load()}>Try again</Button>}>{error}</InlineNotice>}
    {loading ? <p className="field-message">Loading property media…</p> : items.length === 0
      ? <p className="field-message">{filter === 'all' ? 'No property media yet.' : `No ${filter}s match this filter.`}</p>
      : <div className="media-grid">{items.map((item) => <button type="button" key={item.id} onClick={() => select(item)} aria-label={`Open ${item.title || item.kind}`}>
        {item.kind === 'photo' && item.thumbnailUrl ? <img src={item.thumbnailUrl} alt="" /> : <span>{item.kind === 'photo' ? <Camera aria-hidden="true" /> : <Play aria-hidden="true" />}</span>}
        <small>{item.title || new Date(item.capturedAt).toLocaleDateString()}</small>
      </button>)}</div>}
  </div>
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`
}
